/**
 * DefensibilityTab — Hamilton Helmer 7 Powers Framework Assessment
 *
 * 7 power cards with strength ratings (0-3), guidance text, and evidence notes.
 * Moat Summary section shows progress, average score, verdict chips, and overall verdict.
 * Data flow: GET on mount, local state updates on rating change, PUT on save.
 */

import { useEffect, useState } from 'react'
import { authFetchJson } from '../../lib/authFetch'
import type {
  DefensibilityAssessment,
  DefensibilityPower,
} from '../../types'
import { DEFENSIBILITY_POWERS, POWER_LABELS } from '../../types'
import { InlineNote } from '../ui/InlineNote'

interface DefensibilityTabProps {
  projectId: number
}

// Power descriptions and assessment guidance
const POWER_GUIDANCE: Record<DefensibilityPower, { description: string; guidance: string }> = {
  scale_economies: {
    description: 'Cost advantages from large scale',
    guidance: 'Does unit cost decrease significantly at scale?',
  },
  network_effects: {
    description: 'Value increases with each user',
    guidance: 'Does each additional user make the product more valuable for others?',
  },
  counter_positioning: {
    description: 'Incumbent can\'t respond without self-harm',
    guidance: 'Would copying this approach damage an incumbent\'s existing business?',
  },
  switching_costs: {
    description: 'High cost to switch to competitor',
    guidance: 'How painful would it be for a customer to switch to a competitor?',
  },
  branding: {
    description: 'Premium from brand perception',
    guidance: 'Can this command a price premium based on brand alone?',
  },
  cornered_resource: {
    description: 'Exclusive access to valuable asset',
    guidance: 'Does this have access to a resource that competitors cannot replicate?',
  },
  process_power: {
    description: 'Superior process built over time',
    guidance: 'Is there a complex process that would take years to replicate?',
  },
}

// Rating color map: 0=gray, 1=red, 2=yellow, 3=green
const RATING_COLORS = {
  0: { bg: '#4a4a6a', text: '#e8e8f0' },
  1: { bg: '#E61B25', text: '#ffffff' },
  2: { bg: '#F59E0B', text: '#000000' },
  3: { bg: '#10B981', text: '#ffffff' },
}

const RATING_LABELS = {
  0: 'N/A',
  1: 'Weak',
  2: 'Moderate',
  3: 'Strong',
}

export function DefensibilityTab({ projectId }: DefensibilityTabProps) {
  const [assessment, setAssessment] = useState<DefensibilityAssessment | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Local state: ratings (0-3) and evidence notes for each power
  const [ratings, setRatings] = useState<Record<DefensibilityPower, number>>({
    scale_economies: 0,
    network_effects: 0,
    counter_positioning: 0,
    switching_costs: 0,
    branding: 0,
    cornered_resource: 0,
    process_power: 0,
  })

  const [notes, setNotes] = useState<Record<DefensibilityPower, string>>({
    scale_economies: '',
    network_effects: '',
    counter_positioning: '',
    switching_costs: '',
    branding: '',
    cornered_resource: '',
    process_power: '',
  })

  const [overallVerdict, setOverallVerdict] = useState('')

  // Fetch assessment on mount
  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        setLoading(true)
        const data = await authFetchJson<{ assessment: DefensibilityAssessment | null }>(
          `/api/v2/projects/${projectId}/defensibility`
        )

        if (data.assessment) {
          const a = data.assessment
          setAssessment(a)
          // Populate ratings
          setRatings({
            scale_economies: a.scale_economies ?? 0,
            network_effects: a.network_effects ?? 0,
            counter_positioning: a.counter_positioning ?? 0,
            switching_costs: a.switching_costs ?? 0,
            branding: a.branding ?? 0,
            cornered_resource: a.cornered_resource ?? 0,
            process_power: a.process_power ?? 0,
          })
          // Populate overall verdict if available
          setOverallVerdict(a.notes ?? '')
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load assessment')
      } finally {
        setLoading(false)
      }
    }

    fetchAssessment()
  }, [projectId])

  const handleRatingChange = (power: DefensibilityPower, value: number) => {
    setRatings((prev) => ({ ...prev, [power]: value }))
  }

  const handleNoteChange = (power: DefensibilityPower, value: string) => {
    setNotes((prev) => ({ ...prev, [power]: value }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)

      const payload = {
        scale_economies: ratings.scale_economies,
        network_effects: ratings.network_effects,
        counter_positioning: ratings.counter_positioning,
        switching_costs: ratings.switching_costs,
        branding: ratings.branding,
        cornered_resource: ratings.cornered_resource,
        process_power: ratings.process_power,
        notes: overallVerdict,
      }

      const data = await authFetchJson<{ assessment: DefensibilityAssessment }>(
        `/api/v2/projects/${projectId}/defensibility`,
        {
          method: 'PUT',
          json: payload,
        }
      )

      setAssessment(data.assessment)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save assessment')
    } finally {
      setSaving(false)
    }
  }

  // Compute moat summary metrics
  const ratedPowers = DEFENSIBILITY_POWERS.filter((p) => ratings[p] > 0)
  const assessedCount = ratedPowers.length
  const averageScore =
    ratedPowers.length > 0
      ? (ratedPowers.reduce((sum, p) => sum + ratings[p], 0) / ratedPowers.length).toFixed(1)
      : '—'

  const verdictChips = DEFENSIBILITY_POWERS.filter((p) => ratings[p] >= 2)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-[#a8a8c0]">Loading assessment...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Error message */}
      {error && (
        <div className="p-4 bg-[rgba(230,27,37,0.1)] border border-[#E61B25] rounded-lg text-[#E61B25] text-sm">
          {error}
        </div>
      )}

      {/* 7 Power Cards */}
      <div className="grid grid-cols-1 gap-4">
        {DEFENSIBILITY_POWERS.map((power) => (
          <div
            key={power}
            className="bg-[#1e1e38] border border-[#2a2a44] rounded-xl p-5 space-y-4"
          >
            {/* Header: Power name + description */}
            <div>
              <h3 className="text-[14px] font-semibold text-[#e8e8f0]">
                {POWER_LABELS[power]}
              </h3>
              <p className="text-[12px] text-[#a8a8c0] mt-1">
                {POWER_GUIDANCE[power].description}
              </p>
            </div>

            {/* Rating buttons: 0=N/A, 1=Weak, 2=Moderate, 3=Strong */}
            <div className="flex gap-2">
              {[0, 1, 2, 3].map((value) => (
                <button
                  key={value}
                  onClick={() => handleRatingChange(power, value)}
                  className={`
                    px-4 py-1.5
                    rounded-lg
                    text-[13px] font-medium
                    transition-all
                    ${
                      ratings[power] === value
                        ? 'text-white'
                        : 'text-[#a8a8c0] border border-[#2a2a44] bg-transparent'
                    }
                  `}
                  style={
                    ratings[power] === value
                      ? {
                          backgroundColor: RATING_COLORS[value as 0 | 1 | 2 | 3].bg,
                          color: RATING_COLORS[value as 0 | 1 | 2 | 3].text,
                        }
                      : undefined
                  }
                >
                  {RATING_LABELS[value as 0 | 1 | 2 | 3]}
                </button>
              ))}
            </div>

            {/* How to assess guidance */}
            <p className="text-[12px] italic text-[#a8a8c0]">
              {POWER_GUIDANCE[power].guidance}
            </p>

            {/* Evidence note */}
            <div>
              <label className="text-[12px] font-medium text-[#a8a8c0] block mb-2">
                Evidence
              </label>
              <InlineNote
                value={notes[power]}
                onChange={(value) => handleNoteChange(power, value)}
                placeholder="Add evidence or analysis for this power..."
              />
            </div>
          </div>
        ))}
      </div>

      {/* Moat Summary Section */}
      <div className="bg-[#12122a] border border-[#2a2a44] rounded-xl p-5 space-y-4">
        <h3 className="text-[14px] font-semibold text-[#e8e8f0]">Moat Summary</h3>

        {/* Progress */}
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-[12px] text-[#a8a8c0]">
              {assessedCount}/7 powers assessed
            </span>
            <span className="text-[12px] text-[#a8a8c0]">
              {Math.round((assessedCount / 7) * 100)}%
            </span>
          </div>
          <div className="w-full h-2 bg-[#1e1e38] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#10B981] transition-all"
              style={{ width: `${(assessedCount / 7) * 100}%` }}
            />
          </div>
        </div>

        {/* Average Score */}
        <div className="flex items-center justify-between">
          <span className="text-[12px] text-[#a8a8c0]">Average Score</span>
          <span className="text-[16px] font-semibold text-[#e8e8f0]">{averageScore}/3.0</span>
        </div>

        {/* Verdict Chips */}
        {verdictChips.length > 0 && (
          <div>
            <p className="text-[12px] text-[#a8a8c0] mb-2">Strong & Moderate Powers</p>
            <div className="flex flex-wrap gap-2">
              {verdictChips.map((power) => {
                const rating = ratings[power]
                const isModerate = rating === 2
                const isStrong = rating === 3
                return (
                  <span
                    key={power}
                    className={`
                      px-3 py-1.5
                      rounded-full
                      text-[12px] font-medium
                      ${
                        isStrong
                          ? 'bg-[#10B981] text-white'
                          : isModerate
                            ? 'bg-[#F59E0B] text-black'
                            : ''
                      }
                    `}
                  >
                    {POWER_LABELS[power]}
                  </span>
                )
              })}
            </div>
          </div>
        )}

        {/* Overall Defensibility Verdict */}
        <div>
          <label className="text-[12px] font-medium text-[#a8a8c0] block mb-2">
            Overall Defensibility Verdict
          </label>
          <InlineNote
            value={overallVerdict}
            onChange={setOverallVerdict}
            placeholder="Summarize the project's moat strength and defensibility..."
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className={`
            px-6 py-2
            rounded-lg
            font-medium
            text-white
            transition-colors
            ${
              saving
                ? 'bg-[#c41520] opacity-50 cursor-not-allowed'
                : 'bg-[#E61B25] hover:bg-[#d41620]'
            }
          `}
        >
          {saving ? 'Saving...' : 'Save Assessment'}
        </button>
      </div>
    </div>
  )
}

export default DefensibilityTab
